export interface CallbackDoc {
  key:
    | 'get_session'
    | 'get_balance'
    | 'debit'
    | 'credit'
    | 'refund'
    | 'shipping_debit'
    | 'shipping_settle';
  name: string;
  description: string;
  method: 'GET' | 'POST' | 'PUT';
  defaultPath: string;
  requestBody?: string;
  successResponse: string;
  errorResponseShape: string;
  /** `id` is absent for codes the partner defines (e.g. SPENDING_LIMIT_REACHED*). */
  errorCodes: Array<{ code: string; id?: number; meaning: string }>;
}

export const CALLBACK_DOCS: CallbackDoc[] = [
  {
    key: 'get_session',
    name: 'Get Session',
    description: 'Validates a player launch token and returns player account information.',
    method: 'POST',
    defaultPath: '/get_session',
    requestBody: JSON.stringify({ token: 'player_token_from_launch' }, null, 2),
    successResponse: JSON.stringify(
      {
        status: 0,
        account_id: 'user_123',
        username: 'player_name',
        country: 'US',
        currency: 'USD',
        balance: 500000, // cents
      },
      null,
      2
    ),
    errorResponseShape: JSON.stringify(
      {
        status: 1,
        error_code: 'INVALID_TOKEN',
        error_id: 101,
        error_message: 'Invalid or expired session token',
      },
      null,
      2
    ),
    errorCodes: [
      { code: 'INVALID_TOKEN', id: 101, meaning: 'Invalid or expired session token' },
      { code: 'INVALID_API_KEY', id: 1001, meaning: 'Invalid API key' },
    ],
  },
  {
    key: 'get_balance',
    name: 'Get Balance',
    description: 'Fetches the current balance and currency for a player account.',
    method: 'POST',
    defaultPath: '/get_balance',
    requestBody: JSON.stringify(
      { token: 'player_token', account_id: 'user_123' },
      null,
      2
    ),
    successResponse: JSON.stringify(
      {
        status: 0,
        balance: 500000, // cents
        currency: 'USD',
      },
      null,
      2
    ),
    errorResponseShape: JSON.stringify(
      {
        status: 1,
        error_code: 'INVALID_TOKEN',
        error_id: 101,
        error_message: 'Invalid or expired session token',
      },
      null,
      2
    ),
    errorCodes: [
      { code: 'INVALID_TOKEN', id: 101, meaning: 'Invalid or expired session token' },
      { code: 'INVALID_PLAYER_ID', id: 2021, meaning: 'Invalid player_id' },
      { code: 'INVALID_API_KEY', id: 1001, meaning: 'Invalid API key' },
    ],
  },
  {
    key: 'debit',
    name: 'Debit',
    description:
      'Deducts an amount from player balance before a loot pack reveal. `game_type` is `loot_pack` for regular packs and `custom_pack` for Custom Pack Lab rounds, where the amount is the quoted price of the player’s selection (expected value ÷ RTP).',
    method: 'POST',
    defaultPath: '/debit',
    requestBody: JSON.stringify(
      {
        token: 'player_token',
        account_id: 'user_123',
        amount: 10000, // cents
        currency: 'USD',
        transaction_id: 'debit_tx_uuid',
        round_id: 'round_uuid',
        game_id: 'loot_pack_id',
        game_name: 'Loot Pack',
        game_type: 'loot_pack', // loot_pack | custom_pack
        note: 'debit',
      },
      null,
      2
    ),
    successResponse: JSON.stringify(
      {
        status: 0,
        balance: 490000, // cents after debit
      },
      null,
      2
    ),
    errorResponseShape: JSON.stringify(
      {
        status: 1,
        error_code: 'INSUFFICIENT_BALANCE',
        error_id: 2028,
        error_message: 'Insufficient balance',
      },
      null,
      2
    ),
    errorCodes: [
      {
        code: 'INSUFFICIENT_BALANCE',
        id: 2028,
        meaning: 'Player does not have enough balance for this transaction',
      },
      {
        code: 'SPENDING_LIMIT_REACHED[_<PERIOD>]',
        meaning:
          'Player hit a spending limit (e.g. _DAY, _WEEK, _MONTH). Passed through to the player UI; your error_message is shown to the player',
      },
      {
        code: 'DUPLICATE_TRANSACTION_ID',
        id: 2041,
        meaning: 'Transaction already processed (idempotent success)',
      },
      { code: 'CURRENCY_MISMATCH', id: 2033, meaning: 'Player currency does not match request currency' },
      { code: 'INVALID_GAME_ID', id: 2022, meaning: 'Invalid game_id' },
      { code: 'ZERO_AMOUNT_VALUE', id: 2034, meaning: 'Zero or null amount not allowed' },
      { code: 'NEGATIVE_AMOUNT_VALUE', id: 2027, meaning: 'Negative amount not allowed' },
      { code: 'INVALID_TOKEN', id: 101, meaning: 'Invalid or expired session token' },
      { code: 'INVALID_API_KEY', id: 1001, meaning: 'Invalid API key' },
    ],
  },
  {
    key: 'credit',
    name: 'Credit',
    description:
      'Adds winnings back to player balance after loot pack reveal. The `reason` field says why we are paying: sale, auto_sale, unavailable_item (warehouse compensation) or shipping_canceled (player canceled a paid delivery). `game_type` is `loot_pack` for regular packs and `custom_pack` for Custom Pack Lab rounds.',
    method: 'POST',
    defaultPath: '/credit',
    requestBody: JSON.stringify(
      {
        token: 'player_token',
        account_id: 'user_123',
        amount: 50000, // cents
        currency: 'USD',
        transaction_id: 'credit_tx_uuid',
        round_id: 'round_uuid',
        game_id: 'loot_pack_id',
        game_name: 'Loot Pack',
        game_type: 'loot_pack', // loot_pack | custom_pack
        round_end_state: true,
        reason: 'sale', // sale | auto_sale | unavailable_item | shipping_canceled
        note: 'credit',
      },
      null,
      2
    ),
    successResponse: JSON.stringify(
      {
        status: 0,
        balance: 540000, // cents after credit
      },
      null,
      2
    ),
    errorResponseShape: JSON.stringify(
      {
        status: 1,
        error_code: 'FAILED_TRANS',
        error_id: 2035,
        error_message: 'Transaction failed',
      },
      null,
      2
    ),
    errorCodes: [
      { code: 'DUPLICATE_TRANSACTION_ID', id: 2041, meaning: 'Transaction already processed (idempotent success)' },
      { code: 'CURRENCY_MISMATCH', id: 2033, meaning: 'Player currency does not match request currency' },
      { code: 'INVALID_GAME_ID', id: 2022, meaning: 'Invalid game_id' },
      { code: 'ZERO_AMOUNT_VALUE', id: 2034, meaning: 'Zero or null amount not allowed' },
      { code: 'NEGATIVE_AMOUNT_VALUE', id: 2027, meaning: 'Negative amount not allowed' },
      { code: 'FAILED_TRANS', id: 2035, meaning: 'Transaction failed at operator backend' },
      { code: 'INVALID_TOKEN', id: 101, meaning: 'Invalid or expired session token' },
      { code: 'INVALID_API_KEY', id: 1001, meaning: 'Invalid API key' },
      {
        code: 'UNSUPPORTED_TRANSACTION',
        id: 2096,
        meaning: 'You do not settle this payout type (e.g. a reason you do not recognise) — permanent, we stop retrying',
      },
    ],
  },
  {
    key: 'refund',
    name: 'Refund',
    description:
      'Refunds a debit transaction when the round is cancelled or abandoned. The `reason` field says why when we can attribute it: unavailable_item, shipping_canceled or money_back — null for unattributed refunds (purchase rollbacks, rows predating the field). `game_type` is `loot_pack` for regular packs and `custom_pack` for Custom Pack Lab rounds. A shipping refund that returns sales tax also carries `tax`: how much of `amount` is tax, by jurisdiction and fee, in cents (`amount`, `state`, `calculation_id`, `jurisdictions`, `fees`) — the card’s share as charged, so refunding every card of a delivery returns exactly what each jurisdiction was charged.',
    method: 'POST',
    defaultPath: '/refund',
    requestBody: JSON.stringify(
      {
        token: 'player_token',
        account_id: 'user_123',
        amount: 10000, // cents to refund
        original_transaction_id: 'original_debit_tx_uuid',
        transaction_id: 'refund_tx_uuid',
        currency: 'USD',
        round_id: 'round_uuid',
        game_id: 'loot_pack_id',
        game_name: 'Loot Pack',
        game_type: 'loot_pack', // loot_pack | custom_pack
        reason: 'unavailable_item', // unavailable_item | shipping_canceled | money_back | null
        note: 'refund',
      },
      null,
      2
    ),
    successResponse: JSON.stringify(
      {
        status: 0,
        balance: 500000, // cents after refund
      },
      null,
      2
    ),
    errorResponseShape: JSON.stringify(
      {
        status: 1,
        error_code: 'FAILED_TRANS',
        error_id: 2035,
        error_message: 'Transaction failed',
      },
      null,
      2
    ),
    errorCodes: [
      { code: 'DUPLICATE_TRANSACTION_ID', id: 2041, meaning: 'Transaction already processed (idempotent success)' },
      { code: 'CURRENCY_MISMATCH', id: 2033, meaning: 'Player currency does not match request currency' },
      { code: 'INVALID_GAME_ID', id: 2022, meaning: 'Invalid game_id' },
      { code: 'ZERO_AMOUNT_VALUE', id: 2034, meaning: 'Zero or null amount not allowed' },
      { code: 'NEGATIVE_AMOUNT_VALUE', id: 2027, meaning: 'Negative amount not allowed' },
      { code: 'FAILED_TRANS', id: 2035, meaning: 'Transaction failed at operator backend' },
      { code: 'INVALID_TOKEN', id: 101, meaning: 'Invalid or expired session token' },
      { code: 'INVALID_API_KEY', id: 1001, meaning: 'Invalid API key' },
      {
        code: 'UNSUPPORTED_TRANSACTION',
        id: 2096,
        meaning: 'You do not settle this payout type (e.g. a reason you do not recognise) — permanent, we stop retrying',
      },
    ],
  },
  {
    key: 'shipping_debit',
    name: 'Shipping Debit',
    description:
      'Charges a player for the physical delivery of their cards. Sent once, immediately, when the player confirms the delivery (off-chain cards only). Same shape as Debit, but on its own path so you can route / meter shipping charges separately. The amount is shipping + handling + sales tax (sales tax only when enabled for your operator). While a shipping promotion is active the amount is the promotional price the player owes, and a delivery that costs the player nothing sends no Shipping Debit at all, so you never receive a zero-amount shipping_debit; shipping_settle still arrives with amount: 0, as for every delivery. The body also carries fees — the shipping, handling and sales_tax parts of amount, integer cents that add up to amount exactly (shipping includes any insurance or signature add-on) — and, only when a shipping promotion applied, promo with its id, label, covers (the parts of the price it covered), regular_amount (what the delivery would have cost without it, chosen add-ons included, in cents) and discount (what it took off, in cents); without a promotion there is no promo key. When sales tax applies to the delivery, the body also carries tax — the tax as you record it, without recalculating: amount (equal to fees.sales_tax), the state it was quoted for, calculation_id, lines (the tax on each card and on the shipping, insurance, signature and handling), jurisdictions (the tax owed to each state, county, city and district) and fees (flat fees such as Colorado’s Retail Delivery Fee); integer cents, the lines add up to amount and so do the jurisdictions and fees together. Without sales tax there is no tax key. A Resend from the console carries the same JSON fields and values and is signed afresh, so always verify the signature against the bytes you receive. If a shipped card later turns out undeliverable, it is bought back via /credit (the standard buyback price, transaction_id crd_<pool_item_id>, carrying round_end_state to close the round) plus /refund for its handling fee and its share of any sales tax — and, only when the entire delivery is undeliverable, an additional /refund for the shipping fee (under a shipping promotion, the shipping the player paid, add-ons included). No new callback is added.',
    method: 'POST',
    defaultPath: '/shipping-debit',
    requestBody: JSON.stringify(
      {
        token: 'player_token',
        account_id: 'user_123',
        amount: 1292, // cents = shipping + handling + sales tax (if enabled)
        currency: 'USD',
        transaction_id: 'ship_4012', // stable idempotency key per delivery
        shipping_id: 4012, // the pool_shipping id (one delivery can span rounds)
        rounds: [
          // the cards this charge covers, with their rounds and fmv (cents)
          { round_id: 'rnd_134', pool_item_id: 7001, fmv: 1999 },
          { round_id: 'rnd_135', pool_item_id: 7002, fmv: 1500 },
        ],
        note: 'shipping',
        fees: { shipping: 992, handling: 300, sales_tax: 0 }, // cents; the parts of amount
      },
      null,
      2
    ),
    successResponse: JSON.stringify(
      {
        status: 0,
        balance: 488708, // cents after charge
      },
      null,
      2
    ),
    errorResponseShape: JSON.stringify(
      {
        status: 1,
        error_code: 'INSUFFICIENT_BALANCE',
        error_id: 2028,
        error_message: 'Insufficient balance',
      },
      null,
      2
    ),
    errorCodes: [
      {
        code: 'INSUFFICIENT_BALANCE',
        id: 2028,
        meaning: 'Player does not have enough balance to pay for shipping (delivery is not created)',
      },
      {
        code: 'DUPLICATE_TRANSACTION_ID',
        id: 2041,
        meaning: 'Transaction already processed (idempotent success)',
      },
      { code: 'CURRENCY_MISMATCH', id: 2033, meaning: 'Player currency does not match request currency' },
      { code: 'ZERO_AMOUNT_VALUE', id: 2034, meaning: 'Zero or null amount not allowed' },
      { code: 'NEGATIVE_AMOUNT_VALUE', id: 2027, meaning: 'Negative amount not allowed' },
      { code: 'INVALID_TOKEN', id: 101, meaning: 'Invalid or expired session token' },
      { code: 'INVALID_API_KEY', id: 1001, meaning: 'Invalid API key' },
    ],
  },
  {
    key: 'shipping_settle',
    name: 'Shipping Settle',
    description:
      'Closes a round once the warehouse has fulfilled the round\'s shipped items. Sent exactly once per round (not per item), only when shipping closes the round, so amount is always 0 and is_round_final is always true. transaction_id is shipclose_<round_id> and the call is idempotent. Fires later than shipping_debit (when staff mark items shipped) and is retried. No shipping-settle is sent for an unavailable card: if a shipped item turns out undeliverable, the operator instead receives /credit for the standard buyback price (transaction_id crd_<pool_item_id>, carrying round_end_state to close the round) plus /refund for that card\'s handling fee and its share of any sales tax — and, only when the entire shipment is undeliverable, an additional /refund for the shipping fee.',
    method: 'POST',
    defaultPath: '/shipping-settle',
    requestBody: JSON.stringify(
      {
        token: 'player_token',
        account_id: 'user_123',
        amount: 0, // always 0 — moves no money
        currency: 'USD',
        transaction_id: 'shipclose_rnd_134', // stable per round
        round_id: 'rnd_134',
        pool_item_ids: [7001, 7002], // the round's physically shipped cards
        status: 'shipped',
        is_round_final: true, // always true — fires only when shipping closes the round
        note: 'shipping_settle',
      },
      null,
      2
    ),
    successResponse: JSON.stringify(
      {
        status: 0,
        balance: 488708, // cents, unchanged (no money moved)
      },
      null,
      2
    ),
    errorResponseShape: JSON.stringify(
      {
        status: 1,
        error_code: 'FAILED_TRANS',
        error_id: 2035,
        error_message: 'Transaction failed',
      },
      null,
      2
    ),
    errorCodes: [
      {
        code: 'DUPLICATE_TRANSACTION_ID',
        id: 2041,
        meaning: 'Transaction already processed (idempotent success)',
      },
      { code: 'FAILED_TRANS', id: 2035, meaning: 'Transaction failed at operator backend' },
      { code: 'INVALID_TOKEN', id: 101, meaning: 'Invalid or expired session token' },
      { code: 'INVALID_API_KEY', id: 1001, meaning: 'Invalid API key' },
    ],
  },
];
